/* eslint-disable complexity */
import React, { useCallback, useEffect, useState } from 'react';
import {
    vendorBasicDetailsFormSchemaWrapper,
    US_CANADA_PHONE,
    INTERNATIONAL_PHONE,
} from 'vendor/constants/schema/VendorBasicDetailsFormSchema';
import { VendorBasicDetailsFormStyled } from './VendorBasicDetailsForm.styled';
import { VendorAccountSetupStepFooter, PearlApps } from 'framework';
import PearlForm from 'vendor/components/forms/PearlForm';
import { usePearlModal, VendorAccountSetupStepLayout, PearlModalSizes } from 'framework';
import { NewVendorWebstoreSetupHeader } from '../WebstoreSetup/NewVendorWebstoreSetupHeader';
import VerificationModalContent from './VerificationModalContent';
import VendorDetailsForm from './VendorDetailsForm';
import { useStore } from 'stores/Store';
import { VendorAuthService } from 'lib/apis/VendorAuthService';
import { TokenService } from 'lib/apis/TokenService';
import { useNavigate } from 'react-router-dom';
import { setNewVendor, useAppName } from 'shared/hooks/AppName';
import { APP_ROUTES } from 'shared/constants/navigation';
import { WebstoreService } from 'shared/lib/apis/WebstoreService';
import { WebstoreService as VendorWebstoreService } from 'vendor/lib/apis/WebstoreService';
import { IconName } from 'dbi-pearl-ui-kit';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PremiumTrialWelcome, { PremiumTrialFooterActions } from './PremiumTrialWelcome';
import { EmailVerifiedToast } from './EmailVerifiedToast';
import { StorageKeys, StorageService } from 'lib/StorageService';
import { PearlAlertBanner } from '../PearlAlertBanner/PearlAlertBanner';
import { NewWebstoreSetupStepForms } from 'vendor/constants/WebstoreSetupSteps';
import { unwrapResponse } from 'lib/apis/utils';
import {
    hasBusinessDetails,
    redirectToVendorConnectOrFallback,
} from 'shared/lib/VendorConnectRedirect';
import { VendorService } from 'vendor/lib/apis/VendorService';
import { UserService } from 'lib/apis/UserService';
import { StripeService } from 'lib/apis/StripeServiceNew';
import { FREE_PREMIUM_TRIAL_ENABLED } from 'shared/constants/featureFlags';
import { hasLivePromotion } from 'shared/hooks/useActivePromotion';

const VENDOR_LOOKUP_RETRIES = 6;
const VENDOR_LOOKUP_DELAY_MS = 500;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchVendorByUsernameWithRetry = async (vendorService, username) => {
    for (let attempt = 0; attempt < VENDOR_LOOKUP_RETRIES; attempt += 1) {
        const updatedVendor = await vendorService.getVendorByUsername(username);
        if (updatedVendor?.id) {
            return updatedVendor;
        }
        if (attempt < VENDOR_LOOKUP_RETRIES - 1) {
            await wait(VENDOR_LOOKUP_DELAY_MS);
        }
    }
    return null;
};

const resolveSignupVendor = async ({ vendorService, userApi, username, vendorStore, authUser }) => {
    if (vendorStore?.id) {
        const existingVendor = await vendorService.getVendorById(vendorStore.id);
        if (existingVendor?.id) {
            vendorStore.setVendor(existingVendor);
            authUser.Roles.Vendor = true;
            return existingVendor;
        }
    }

    let resolvedVendor = await fetchVendorByUsernameWithRetry(vendorService, username);
    if (!resolvedVendor?.id) {
        resolvedVendor = await userApi.ensureVendor();
    }

    if (resolvedVendor?.id) {
        vendorStore.setVendor(resolvedVendor);
        authUser.Roles.Vendor = true;
    }

    return resolvedVendor;
};

const WelcomePageShell = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    min-height: 100dvh;
    width: 100%;
    box-sizing: border-box;

    form {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        min-height: 0;
    }

    @media (max-width: 815px) {
        min-height: 100dvh;
        height: 100dvh;
        overflow: hidden;
    }
`;

export const Footer = ({
    handleSubmit,
    isLetsGo,
    isSubmit,
    handleContinue,
    showAlertBanner,
    handleCloseAlert,
    submitButtonText,
    isPremiumTrialWelcome,
    onContinueTrial,
    onPickPlan,
    trialLoading,
}) => {
    const { t } = useTranslation();

    if (isPremiumTrialWelcome) {
        return (
            <VendorAccountSetupStepFooter
                progress={0}
                showBack={false}
                pearlConnectFooter
                premiumTrialWelcomeFooter
                footerContent={
                    <PremiumTrialFooterActions
                        className="navigationButtonWrapper premiumTrialWelcomeActions premiumTrialWelcomeDesktopFooter"
                        onContinueTrial={onContinueTrial}
                        onPickPlan={onPickPlan}
                        trialLoading={trialLoading}
                    />
                }
            />
        );
    }

    return (
        <>
            <VendorAccountSetupStepFooter
                progress={''}
                isSubmit={isSubmit}
                isLetsGo={isLetsGo}
                handleSubmit={handleSubmit}
                hideStepCount={false}
                showBack={false}
                handleContinue={handleContinue}
                submitButtonText={submitButtonText}
                pearlConnectFooter
            />
            {showAlertBanner && (
                <PearlAlertBanner
                    handleOnClose={handleCloseAlert}
                    bodyText={t('Forms.BasicDetails.IncorrectEmailText')}
                    subTextBullets={[
                        <>
                            {t('Forms.BasicDetails.ContactSupportText')}{' '}
                            <u>{t('Forms.BasicDetails.VendorSupportEmail')}</u>
                        </>,
                    ]}
                    icon={IconName.WARNING}
                    type={'success'}
                />
            )}
        </>
    );
};

// isSignature: the Signature Collection signup page. It looks the same, but the vendor is created
// as a signature vendor and, once the email is verified, goes straight to the Signature plans
// (no welcome / free-trial screen, since signature vendors have no trial).
const VendorBasicDetailsForm = ({ isSignature = false }) => {
    const { t } = useTranslation();
    const { appName } = useAppName();
    const { openModal, closeModal } = usePearlModal();
    const [welcomeBanner, setWelcomeBanner] = useState(false);
    const [showEmailVerifiedToast, setShowEmailVerifiedToast] = useState(false);

    const dismissEmailVerifiedToast = useCallback(() => {
        setShowEmailVerifiedToast(false);
    }, []);
    const [signUpError, setSignUpError] = useState('');
    const [isPasswordStrong, setIsPasswordStrong] = useState(false);
    const webstoreApi = WebstoreService();
    const { eventUser, event, authUser, vendor, webstore } = useStore();
    const formName = welcomeBanner ? 'WelcomeBanner' : 'BasicDetails';
    const navigate = useNavigate();
    const [showAlertBanner, setShowAlertBanner] = useState(false);
    const [trialLoading, setTrialLoading] = useState(false);
    const [trialError, setTrialError] = useState('');
    const storageService = StorageService();

    const cybInProgress = JSON.parse(storageService?.get(StorageKeys.cybInProgressWebstore));
    const crawledVendorCybInProgress = JSON.parse(storageService?.get('crawledVendor'));
    console.log('cybInProgress', crawledVendorCybInProgress);
    const formOptions = {
        mode: 'onChange',
        defaultValues: {},
    };

    useEffect(() => {
        setNewVendor('newVendor');
        return () => {
            closeModal();
        };
    }, []);

    const navigateAfterSignup = async ({ nextSetupStep } = {}) => {
        await eventUser.Reaction;
        await event.Reaction;
        await vendor.Reaction;
        const tokenService = TokenService();
        if (appName === PearlApps.VENDOR) {
            if (vendor && vendor.id) {
                if (hasBusinessDetails(webstore)) {
                    redirectToVendorConnectOrFallback(tokenService, navigate);
                } else {
                    navigate(
                        `${APP_ROUTES.vendor.webstore.newsetup}/${
                            nextSetupStep ?? NewWebstoreSetupStepForms.BUSINESS
                        }`
                    );
                }
            } else {
                navigate(APP_ROUTES.vendor.auth.welcome);
            }
        } else {
            navigate(APP_ROUTES.landing);
        }
    };

    const onVerifyHandler = async () => {
        await vendor.Reaction;
        if (isSignature) {
            handlePickPlan();
            return;
        }
        // Free trial discontinued: the welcome screen now only exists to offer the sale, so with
        // no sale running the vendor goes straight to the plans.
        if (!FREE_PREMIUM_TRIAL_ENABLED && !(await hasLivePromotion())) {
            handlePickPlan();
            return;
        }
        setWelcomeBanner(true);
        setShowEmailVerifiedToast(true);
    };

    const openVerificationModal = () => {
        if (true) {
            openModal(
                <VerificationModalContent onVerify={onVerifyHandler} email={authUser.Email} />,
                PearlModalSizes.VERIFY_EMAIL,
                '',
                null,
                false,
                '0',
                0,
                true,
                0.6,
                12,
                'stretch'
            );
        }
    };

    const handleContinue = async () => {
        await navigateAfterSignup();
    };

    const handleContinueTrial = async () => {
        setTrialLoading(true);
        setTrialError('');

        try {
            await vendor.Reaction;
            const vendorService = VendorService();
            const userApi = UserService();
            const activeVendor = await resolveSignupVendor({
                vendorService,
                userApi,
                username: authUser.Username,
                vendorStore: vendor,
                authUser,
            });

            if (!activeVendor?.id) {
                setTrialError('Your account is still being set up. Please try again in a moment.');
                return;
            }

            const stripeService = StripeService();
            const trialResponse = await stripeService.createPremiumTrialSubscription(
                vendor.name || authUser?.Username || 'Premium Trial',
                authUser?.Email,
                vendor.id
            );
            const { subscriptionId, trialEnd } = trialResponse?.data || {};

            let refreshedStripeData;
            try {
                refreshedStripeData = await vendorService.getStripeData(vendor.id);
            } catch (error) {
                console.error('Unable to refresh Stripe data after premium trial signup', error);
            }
            vendor.updateVendor({
                stripeData: {
                    ...(vendor.stripeData || {}),
                    subscriptionId,
                    trialEnd,
                    ...refreshedStripeData,
                },
            });

            try {
                const userWebstore = await webstoreApi.getWebstoreByUserId(authUser.Id);
                if (userWebstore?.id) {
                    webstore.setFromApi(userWebstore);
                }
            } catch (error) {
                console.error('Unable to refresh webstore after premium trial signup', error);
            }

            if (webstore.SubscriptionTier !== 'premium') {
                webstore.SubscriptionTier = 'premium';
            }
            webstore.CurrentSetupStep = NewWebstoreSetupStepForms.SUCCESS;
            if (webstore.Id) {
                const vendorWebstoreApi = VendorWebstoreService();
                const patchResponse = await vendorWebstoreApi.patchWebstore(
                    webstore.forPatchSetupStep
                );
                const patchedStore = unwrapResponse(patchResponse);
                if (patchedStore) {
                    webstore.setFromApi(patchedStore);
                }
            }

            await navigateAfterSignup({ nextSetupStep: NewWebstoreSetupStepForms.SUCCESS });
        } catch (error) {
            setTrialError(
                typeof error === 'string'
                    ? error
                    : error?.message ||
                          'Unable to start Premium trial. Please try again or choose a plan.'
            );
        } finally {
            setTrialLoading(false);
        }
    };

    const handlePickPlan = () => {
        navigate(`${APP_ROUTES.vendor.webstore.newsetup}/${NewWebstoreSetupStepForms.PLAN}`);
    };

    const handlePasswordStrengthChange = (isStrong) => {
        setIsPasswordStrong(isStrong);
    };

    const handleCloseAlert = () => {
        setShowAlertBanner(false);
    };

    const handleRegistration = async (data) => {
        const {
            Emailaddress,
            Enterpassword,
            Firstname,
            Lastname,
            Phonenumber,
            Confirmpassword,
            Agree,
        } = data;
        if (
            !Agree ||
            !Emailaddress ||
            !Enterpassword ||
            !Firstname ||
            !Lastname ||
            !Phonenumber ||
            !Confirmpassword
        ) {
            return;
        }
        if (Enterpassword !== Confirmpassword) {
            return;
        }
        if (!isPasswordStrong) {
            return;
        }
        if (cybInProgress && cybInProgress?.cybContactEmail !== Emailaddress) {
            setShowAlertBanner(true);
            return null;
        }
        const isFirstNameValid = /[0-9]|[^a-zA-Z0-9 ]/.test(Firstname);
        if (isFirstNameValid) {
            return;
        }
        const isLastNameValid = /[0-9]|[^a-zA-Z0-9 ]/.test(Lastname);
        if (isLastNameValid) {
            return;
        }
        const isPhoneNumberValid = (isSignature ? INTERNATIONAL_PHONE : US_CANADA_PHONE).test(
            Phonenumber
        );
        if (!isPhoneNumberValid) {
            return;
        }
        authUser.updateAuthUserFromNewSignup(data);
        authUser.email = Emailaddress;

        const vendorAuthService = VendorAuthService();
        try {
            const response = await vendorAuthService.signUpVendor(
                Emailaddress,
                Enterpassword,
                '+' + Phonenumber,
                Firstname,
                Lastname,
                isSignature ? 'signature' : undefined
            );
            setSignUpError('');
            if (response?.data) {
                const tokenService = TokenService();
                const {
                    data: { authResult: tokens, user: currentUser },
                } = response;
                tokenService.setAccessToken(tokens.AccessToken);
                tokenService.setIdToken(tokens.IdToken);
                tokenService.setRefreshToken(tokens.RefreshToken);
                tokenService.setExpiry(tokens.ExpiresIn);
                try {
                    if (currentUser?.id) {
                        authUser.setAuthUser(currentUser);
                        const userWebstore = await webstoreApi.getWebstoreByUserId(authUser.Id);

                        if (!!userWebstore?.id) {
                            authUser.Roles.Vendor = true;
                            webstore.setFromApi(userWebstore);
                        }
                    }
                } catch (e) {
                    console.error('error', e);
                }

                openVerificationModal();
            }
        } catch (error) {
            setSignUpError(error);
        }
    };

    const vendorBusinessDetailsFormSchema = vendorBasicDetailsFormSchemaWrapper({
        allowInternationalPhone: isSignature,
    });

    const pearlForm = (
        <PearlForm
            StyledForm={VendorBasicDetailsFormStyled}
            formName={formName}
            formOptions={formOptions}
            validationSchema={vendorBusinessDetailsFormSchema}
        >
            {!welcomeBanner ? (
                <VendorAccountSetupStepLayout
                    header={
                        <NewVendorWebstoreSetupHeader
                            stepName={formName}
                            hideStepCount={true}
                            hideStepName={true}
                            showSubTitle={true}
                            showMobileTopBar={true}
                            pearlConnectStepTheme
                            announcementText="Once submitted, we will gather the necessary details to get you up and running as quickly as possible."
                        />
                    }
                    formComponent={
                        <VendorDetailsForm
                            signUpError={signUpError}
                            onPasswordStrengthChange={handlePasswordStrengthChange}
                        />
                    }
                    footer={
                        <Footer
                            isSubmit={true}
                            handleSubmit={handleRegistration}
                            showAlertBanner={showAlertBanner}
                            handleCloseAlert={handleCloseAlert}
                            submitButtonText={t('Forms.Labels.Continue')}
                        />
                    }
                />
            ) : welcomeBanner ? (
                <VendorAccountSetupStepLayout
                    compactScroll={welcomeBanner}
                    compactWelcomeHeader={welcomeBanner}
                    header={
                        <NewVendorWebstoreSetupHeader
                            stepName={formName}
                            hideStepCount={true}
                            hideStepName={welcomeBanner}
                            hideTitle={welcomeBanner}
                            showSubTitle={false}
                            showMobileTopBar
                            pearlConnectStepTheme
                            compactWelcomeHeader={welcomeBanner}
                        />
                    }
                    formComponent={
                        <PremiumTrialWelcome
                            trialError={trialError}
                            onContinueTrial={handleContinueTrial}
                            onPickPlan={handlePickPlan}
                            trialLoading={trialLoading}
                        />
                    }
                    footer={
                        <Footer
                            isPremiumTrialWelcome
                            handleContinue={handleContinue}
                            onContinueTrial={handleContinueTrial}
                            onPickPlan={handlePickPlan}
                            trialLoading={trialLoading}
                        />
                    }
                />
            ) : null}
        </PearlForm>
    );

    if (welcomeBanner) {
        return (
            <>
                {showEmailVerifiedToast && (
                    <EmailVerifiedToast
                        message={t('Forms.WelcomeBanner.Message')}
                        onDismiss={dismissEmailVerifiedToast}
                    />
                )}
                <WelcomePageShell>{pearlForm}</WelcomePageShell>
            </>
        );
    }

    return pearlForm;
};

export { VendorBasicDetailsForm };
